/* @flow */
import React, { createContext, type Node, PureComponent } from 'react'
import Cookie from '../components/utils/Cookie'
import ExternalService from '../components/utils/ExternalService'
import request from '../components/utils/Request'
import { getAdsPersonalizationConsent } from '../../lib/utils/didomiConsent'
import { sendWidgetClick } from '../components/utils/ga4Events'

type EmailProviderProps = {|
  children: Node,
  options: {
    proxyOptions: Object,
    serviceUrlPattern: string,
    xhrLogoutUri: string,
    isTracked: boolean,
    isEcoIdentityVariant: boolean,
    cookieNameConstants: Object,
    cookies: Object
  }
|}

type EmailProviderState = {|
  inbox: Object,
  userInfo: {
    email?: string,
    firstName?: string,
    lastName?: string,
    avatar?: string
  },
  isLoading: boolean
|}

const RELOAD_INTERVAL_MS = 120000

const EmailContext: Object = createContext()

const EmailConsumer = EmailContext.Consumer

class EmailProvider extends PureComponent<EmailProviderProps, EmailProviderState> {
  emailRefreshingInterval: IntervalID
  handleLogoutClick: (event: Object) => void
  constructor (props: EmailProviderProps) {
    super(props)

    this.state = {
      inbox: {},
      userInfo: {},
      isLoading: false
    }

    this.handleLogoutClick = this.handleLogoutClick.bind(this)
  }

  componentDidMount () {
    this.loadInbox()
    this.loadUserInfo()
  }

  loadInbox () {
    const {
      options: {
        proxyOptions,
        serviceUrlPattern,
        isEcoIdentityVariant
      }
    } = this.props

    this.setState({
      isLoading: true
    })

    if ((isEcoIdentityVariant && this.shouldProcessInboxRequestEcoIdentity()) || this.shouldProcessInboxRequest()) {
      const serviceHelper = new ExternalService(proxyOptions)
      const serviceUri = serviceHelper.getServiceUrl(serviceUrlPattern)
      this.loadInboxForFirstTime(serviceUri)
      this.startPeriodicallyLoadingInbox(serviceUri)
    }

    this.setState({
      isLoading: false
    })
  }

  loadUserInfo () {
    const {
      options: {
        isEcoIdentityVariant
      }
    } = this.props

    if (isEcoIdentityVariant && this.shouldProcessUserInfoRequest()) {
      this.loadIdentitaUserInfo()
    }
  }

  shouldProcessInboxRequest () {
    const {
      options: {
        cookieNameConstants
      }
    } = this.props

    const cookieHandler = new Cookie()
    const sessionCookieName = cookieNameConstants.sessionCookie
    const uprofileCookieName = cookieNameConstants.uprofileCookie

    return cookieHandler.isSet(sessionCookieName) || cookieHandler.isSet(uprofileCookieName)
  }

  shouldProcessInboxRequestEcoIdentity () {
    const {
      options: {
        cookieNameConstants,
        cookies
      }
    } = this.props

    const sessionCookieName = cookieNameConstants.sessionCookie
    const uprofileCookieName = cookieNameConstants.uprofileCookie

    return cookies[sessionCookieName] || cookies[uprofileCookieName]
  }

  shouldProcessUserInfoRequest () {
    const {
      options: {
        cookieNameConstants,
        cookies
      }
    } = this.props

    const sessionCookieName = cookieNameConstants.sessionCookie
    const aprofileCookieName = cookieNameConstants.aprofileCookie

    return cookies[sessionCookieName] || cookies[aprofileCookieName]
  }

  loadInboxForFirstTime (serviceUri: string) {
    this.sendRequest(serviceUri).then(inbox => {
      this.setState({
        inbox
      })
      window.localStorage.setItem('tracked', 'true')
      const {
        'user-service': userService,
        'user-uuid': userUuid
      } = inbox
      if (userUuid && userService) {
        window.didomiOnReady.push(() => {
          if (getAdsPersonalizationConsent()) {
            this.setCpexTuple(userUuid, userService)
          }
        })
      }
    })
  }

  startPeriodicallyLoadingInbox (serviceUri: string) {
    this.emailRefreshingInterval = setInterval(() => {
      this.sendRequest(serviceUri).then(inbox => {
        this.setState({
          inbox
        })
      }).catch(() => {
      })
    }, RELOAD_INTERVAL_MS)
  }

  sendRequest (serviceUri: string): Promise<Object> {
    return new Promise((resolve, reject) => {
      const config = {
        url: serviceUri,
        withCredentials: true,
        proxy: false
      }

      request(config).then(response => {
        const inbox = (typeof response.data === 'object') ? response.data : {}
        return resolve(inbox)
      }).catch(error => {
        return reject(error)
      })
    })
  }

  handleLogoutClick (event: Object) {
    this.setState({
      isLoading: true
    })

    const {
      options: {
        xhrLogoutUri
      }
    } = this.props
    clearInterval(this.emailRefreshingInterval)
    const eventTargetUri = event.target.getAttribute('href')
    this.sendLogoutRequest(xhrLogoutUri).then(() => {
      this.loadInbox()
    }).catch(() => {
      document.location.href = eventTargetUri
      this.setState({
        isLoading: false
      })
    })
    sendWidgetClick({
      name: 'email',
      click: {
        type: 'link',
        value: 'logout'
      }
    })
  }

  sendLogoutRequest (logoutUri: string): Promise<void> {
    return new Promise((resolve, reject) => {
      request({
        url: logoutUri,
        withCredentials: true
      }).then(() => {
        return resolve()
      }).catch(error => {
        return reject(error)
      })
    })
  }

  setCpexTuple (userUuid: string, userService: string) {
    const {
      options: {
        isTracked
      }
    } = this.props

    if (isTracked && window) {
      window.eco = {
        token: userUuid,
        service: userService
      }
      window.dispatchEvent(new Event('ecoIdReady'))
    }
  }

  async loadIdentitaUserInfo () {
    fetch('/userinfo').then((response) =>
      response.json().then(({
        email,
        firstname,
        lastname,
        avatar
      }) => {
        this.setState({
          userInfo: {
            email,
            firstName: firstname,
            lastName: lastname,
            avatar
          }
        })
      })
    )
  }

  render () {
    const {
      props: {
        children
      },
      state
    } = this

    const contextValue = Object.assign({}, state, {
      handleLogoutClick: this.handleLogoutClick
    })

    return (
      <EmailContext.Provider value={contextValue}>
        { children }
      </EmailContext.Provider>
    )
  }
}

export {
  EmailProvider,
  EmailConsumer
}
